<script>
  /** Specify the textarea value */
  export let value = "";

  /** Specify the placeholder text */
  export let placeholder = "";

  /** Specify the number of cols */
  export let cols = 0;

  /** Specify the number of rows */
  export let rows = 4;

  /**
   * Specify the max character count
   * @type {number}
   */
  export let maxCount = undefined;

  /** Set to `true` to disable the input */
  export let disabled = false;

  /** Set to `true` to mark the field as required */
  export let required = false;

  /** Specify the autocomplete attribute */
  export let autocomplete = undefined;

  /** Set to `true` to use the read-only variant */
  export let readonly = false;

  /** Specify helper/description text */
  export let description = null;

  /** Specify the label text */
  export let labelText = "";

  /**
   * Specify the tooltip text for the label
   * @type {string}
   * @default ""
   */
  export let tooltipText = "";

  /**
   * Specify the tooltip direction
   * @type {"top" | "right" | "bottom" | "left"}
   * @default "right"
   */
  export let tooltipDirection = "right";

  /** Set to `true` to indicate an invalid state */
  export let invalid = false;

  /** Specify the text for the invalid state */
  export let invalidText = "";

  /** Set an id for the textarea element */
  export let id = "ccs-" + Math.random().toString(36);

  /**
   * Specify the resize behavior for the textarea
   * @type {'none' | 'both' | 'horizontal' | 'vertical'}
   * @default 'vertical'
   */
  export let resize = "vertical";
  /**
   * Specify a name attribute for the input
   * @type {string}
   */
  export let name = undefined;

  /** Obtain a reference to the textarea HTML element */
  export let ref = null;

  export let richText = false;

  /** Use compact toolbar buttons and icons when richText is enabled. */
  export let smallToolbar = false;

  /** Options for Add person. People display name and email and insert name.
   * @type {Array<string | { id: string, name: string, email: string } | { label: string, value: string }>}
   */
  export let persons = [];

  /**
   * Options for the label select.
   * @type {Array<{ value: string | number, text: string, disabled?: boolean }> | null}
   */
  export let selectValues = null;

  /**
   * Selected option value for the label select.
   * @type {string | number}
   */
  export let selectedValue = "";

  import WarningFilled from "../icons/WarningFilled.svelte";
  import Tooltip from "../Tooltip/Tooltip.svelte";
  import Button from "../Button/Button.svelte";
  import TextBold from "../icons/TextBold.svelte";
  import TextItalic from "../icons/TextItalic.svelte";
  import TextUnderline from "../icons/TextUnderline.svelte";
  import Paragraph from "../icons/Paragraph.svelte";
  import Heading from "../icons/Heading.svelte";
  import TextColor from "../icons/TextColor.svelte";
  import Link from "../icons/Link.svelte";
  import Select from "../Select/Select.svelte";
  import SelectItem from "../Select/SelectItem.svelte";
  import { tick } from "svelte";

  $: console.log({ selectedValue });

  function updateSelectedValue(event) {
    const value = event.currentTarget.value;
    const option = selectValues?.find((item) => String(item.value) === value);
    selectedValue = option ? option.value : value;
  }

  const removePreviousErrors = () => {
    if (invalid) {
      description = "";
    }
    invalid = false; // reset invalid when the user clicks on the form element
  };

  let editor;
  let savedRange;
  let formats = {};
  let color = "#161616";
  let colorPicker;
  let selectedPerson = "";
  async function insertPerson(event) {
    const selectedValue = event.currentTarget.value;
    if (disabled || readonly || selectedValue === "") return;
    const person = persons[Number(selectedValue)];
    if (person === undefined) return;
    selectedPerson = selectedValue;
    if (!savedRange || !editor.contains(savedRange.commonAncestorContainer)) {
      savedRange = document.createRange();
      savedRange.selectNodeContents(editor);
      savedRange.collapse(false);
    }
    command(
      "insertText",
      typeof person === "string"
        ? person
        : "name" in person
        ? person.name
        : person.value
    );
    await tick();
    selectedPerson = "";
  }

  function selectedLink() {
    const node = savedRange?.commonAncestorContainer;
    const element = node?.nodeType === 1 ? node : node?.parentElement;
    const link = element?.closest("a");
    return link && editor?.contains(link) ? link : null;
  }

  function addLink() {
    if (!editor || disabled || readonly) return;
    saveSelection();
    if (
      !savedRange ||
      !editor.contains(savedRange.commonAncestorContainer) ||
      !savedRange.toString().trim()
    )
      return;
    const entered = window.prompt(
      "URL del enlace:",
      selectedLink()?.getAttribute("href") || "https://"
    );
    if (!entered?.trim()) return;
    const url = entered.trim();
    const normalized =
      /^[a-z][a-z\d+.-]*:/i.test(url) || /^[#/?]/.test(url)
        ? url
        : `https://${url}`;
    try {
      if (
        !["http:", "https:", "mailto:", "tel:"].includes(
          new URL(normalized, document.baseURI).protocol
        )
      )
        throw new Error("Unsupported protocol");
    } catch {
      window.alert("Introduce un enlace válido (http, https, mailto o tel).");
      return;
    }
    command("createLink", normalized);
  }

  function openColorPicker() {
    if (disabled || readonly) return;
    saveSelection();
    colorPicker.click();
  }

  function setColor(event) {
    if (disabled || readonly) return;
    color = event.currentTarget.value;
    command("foreColor", color);
  }

  function updateFormats() {
    formats = {
      bold: document.queryCommandState("bold"),
      italic: document.queryCommandState("italic"),
      underline: document.queryCommandState("underline"),
      heading: document.queryCommandValue("formatBlock").toLowerCase() === "h1",
      link: !!selectedLink(),
    };
  }

  function handleInput() {
    value = editor.innerHTML;
    saveSelection();
  }

  function saveSelection() {
    const sel = window.getSelection();
    if (
      editor &&
      sel &&
      sel.rangeCount &&
      editor.contains(sel.anchorNode) &&
      editor.contains(sel.focusNode)
    ) {
      savedRange = sel.getRangeAt(0).cloneRange();
      updateFormats();
    }
  }

  function restoreSelection() {
    if (!savedRange || !editor.contains(savedRange.commonAncestorContainer))
      return;
    const selection = window.getSelection();
    // Replacing an unchanged caret resets pending typing formats in browsers.
    // Keep it in place so successive toolbar commands can be combined.
    if (selection.rangeCount) {
      const current = selection.getRangeAt(0);
      if (
        current.startContainer === savedRange.startContainer &&
        current.startOffset === savedRange.startOffset &&
        current.endContainer === savedRange.endContainer &&
        current.endOffset === savedRange.endOffset
      )
        return;
    }
    selection.removeAllRanges();
    selection.addRange(savedRange);
  }

  function command(name, arg = null) {
    if (!editor || disabled || readonly) return;
    if (document.activeElement !== editor) editor.focus();
    restoreSelection();
    if (name === "foreColor") {
      const useCSS = document.queryCommandState("styleWithCSS");
      document.execCommand("styleWithCSS", false, true);
      try {
        document.execCommand(name, false, arg);
      } finally {
        document.execCommand("styleWithCSS", false, useCSS);
      }
    } else {
      document.execCommand(name, false, arg);
    }
    value = editor.innerHTML;
    saveSelection();
  }

  $: helperId = `helper-${id}`;
  $: errorId = `error-${id}`;
  $: if (editor && richText && editor.innerHTML !== value) {
    editor.innerHTML = value || "";
    savedRange = null;
  }
</script>

<svelte:document on:selectionchange="{saveSelection}" />

<!-- svelte-ignore a11y-mouse-events-have-key-events -->
<!-- svelte-ignore a11y-click-events-have-key-events -->
<!-- svelte-ignore a11y-no-static-element-interactions -->
<div
  on:click
  on:mouseover
  on:mouseenter
  on:mouseleave
  class:bx--form-item="{true}"
  class:bx--form-item-richText="{richText}"
>
  {#if labelText || $$slots.labelText || selectValues}
    <div class:bx--text-area__label-wrapper="{true}">
      {#if labelText || $$slots.labelText}
        <label
          for="{id}"
          class:bx--label="{true}"
          class:bx--label--disabled="{disabled}"
        >
          <slot name="labelText">
            {labelText}{required ? "*" : ""}
            {#if tooltipText}
              <Tooltip text="{tooltipText}" direction="{tooltipDirection}" />
            {/if}
          </slot>
        </label>
      {/if}
      {#if maxCount}
        <div class:bx--label="{true}" class:bx--label--disabled="{disabled}">
          {value.length}/{maxCount}
        </div>
      {/if}
      {#if selectValues && selectValues.length > 0}
        <div class="select-wrapper">
          <Select
            selected="{selectedValue}"
            inline
            color="dark"
            leftAlign
            fitcontent
            disabled="{disabled || readonly}"
            items="{selectValues}"
            on:change="{updateSelectedValue}"
          >
            {#each selectValues as option}
              <SelectItem
                value="{option.value}"
                text="{option.text}"
                disabled="{option.disabled}"
              />
            {/each}
          </Select>
        </div>
      {/if}
    </div>
  {/if}
  <div
    class:bx--text-area__input-wrapper="{true}"
    class:rich-text-frame="{richText}"
    style:width="{cols
      ? richText
        ? `calc(${cols}ch + 2rem + 2px)`
        : "fit-content"
      : "100%"}"
    style:resize="{resize}"
    data-invalid="{invalid || undefined}"
  >
    {#if richText}
      <!-- svelte-ignore a11y-no-static-element-interactions -->
      <div
        class:toolbar="{true}"
        class:small-toolbar="{smallToolbar}"
        class:bx--text-area--richText="{richText}"
        on:mousedown|preventDefault
      >
        <Button
          kind="ghost"
          size="{smallToolbar ? 'small' : 'default'}"
          disabled="{disabled || readonly}"
          aria-pressed="{!!formats.bold}"
          iconDescription="Bold"
          icon="{TextBold}"
          on:click="{() => command('bold')}"
        />
        <Button
          kind="ghost"
          size="{smallToolbar ? 'small' : 'default'}"
          disabled="{disabled || readonly}"
          aria-pressed="{!!formats.italic}"
          iconDescription="Italic"
          icon="{TextItalic}"
          on:click="{() => command('italic')}"
        />
        <Button
          kind="ghost"
          size="{smallToolbar ? 'small' : 'default'}"
          disabled="{disabled || readonly}"
          aria-pressed="{!!formats.underline}"
          iconDescription="Underline"
          icon="{TextUnderline}"
          on:click="{() => command('underline')}"
        />
        <Button
          kind="ghost"
          size="{smallToolbar ? 'small' : 'default'}"
          disabled="{disabled || readonly}"
          aria-pressed="{!!formats.heading}"
          iconDescription="Heading"
          aria-label="Heading"
          icon="{Heading}"
          on:click="{() => command('formatBlock', 'h1')}"
        />
        <Button
          kind="ghost"
          size="{smallToolbar ? 'small' : 'default'}"
          disabled="{disabled || readonly}"
          aria-pressed="{false}"
          iconDescription="Remove Heading"
          aria-label="Remove Heading"
          icon="{Paragraph}"
          on:click="{() => command('formatBlock', 'p')}"
        />
        <Button
          kind="ghost"
          size="{smallToolbar ? 'small' : 'default'}"
          disabled="{disabled || readonly}"
          aria-pressed="{!!formats.link}"
          aria-label="Link"
          iconDescription="Link"
          icon="{Link}"
          on:click="{addLink}"
        />
        <Button
          kind="ghost"
          size="{smallToolbar ? 'small' : 'default'}"
          disabled="{disabled || readonly}"
          aria-label="Text color"
          iconDescription="Text color"
          on:click="{openColorPicker}"
        >
          <TextColor slot="icon" barColor="{color}" class="bx--btn__icon" />
        </Button>
        <input
          class="color-picker"
          type="color"
          aria-label="Choose text color"
          tabindex="-1"
          disabled="{disabled || readonly}"
          value="{color}"
          bind:this="{colorPicker}"
          on:change="{setColor}"
        />
        {#if persons.length > 0}
          <!-- svelte-ignore a11y-no-static-element-interactions -->
          <div on:mousedown|stopPropagation="{saveSelection}">
            <Select
              inline
              class="person-select"
              labelText="Add person"
              hideLabel
              bind:selected="{selectedPerson}"
              size="sm"
              disabled="{disabled || readonly || persons.length === 0}"
              on:focus="{saveSelection}"
              on:change="{insertPerson}"
            >
              <SelectItem value="" text="Add person" disabled placeholder />
              {#each persons as person, index}
                <SelectItem
                  value="{String(index)}"
                  text="{typeof person === 'string'
                    ? person
                    : 'name' in person
                    ? [person.name, person.email && `(${person.email})`]
                        .filter(Boolean)
                        .join(' ')
                    : person.label}"
                />
              {/each}
            </Select>
          </div>
        {/if}
      </div>
    {/if}
    <div
      class:bx--text-area__wrapper="{true}"
      class:rich-text-body="{richText}"
      style:min-height="{richText
        ? `calc(${Math.max(1, rows) * 1.5}em + 1.375rem)`
        : undefined}"
      data-invalid="{invalid || undefined}"
    >
      {#if invalid}
        <WarningFilled class="bx--text-area__invalid-icon" />
      {/if}
      {#if richText}
        <div
          class="content"
          class:bx--text-area="{true}"
          contenteditable="{!disabled && !readonly}"
          role="textbox"
          aria-multiline="true"
          aria-label="{labelText || placeholder || undefined}"
          aria-disabled="{disabled}"
          aria-readonly="{readonly}"
          id="{id}"
          tabindex="{disabled ? -1 : 0}"
          spellcheck="false"
          data-placeholder="{placeholder}"
          bind:this="{editor}"
          on:input="{handleInput}"
          on:keyup="{saveSelection}"
          on:mouseup="{saveSelection}"
          on:focus="{removePreviousErrors}"
        ></div>
      {:else}
        <textarea
          bind:this="{ref}"
          bind:value="{value}"
          aria-invalid="{invalid || undefined}"
          aria-describedby="{invalid
            ? errorId
            : description
            ? helperId
            : undefined}"
          autocomplete="{autocomplete}"
          disabled="{disabled}"
          id="{id}"
          name="{name}"
          cols="{cols}"
          rows="{rows}"
          placeholder="{placeholder}"
          required="{required}"
          readonly="{readonly}"
          class:bx--text-area="{true}"
          class:bx--text-area-full-width="{!cols}"
          class:bx--text-area--invalid="{invalid}"
          maxlength="{maxCount ?? undefined}"
          {...$$restProps}
          on:click="{removePreviousErrors}"
          on:focus="{removePreviousErrors}"
          on:change
          on:input
          on:keydown
          on:keyup
          on:blur
          on:paste></textarea>
      {/if}
    </div>
  </div>
  {#if !invalid && description}
    <div
      id="{helperId}"
      class:bx--form__helper-text="{true}"
      class:bx--form__helper-text--disabled="{disabled}"
    >
      {description}
    </div>
  {/if}
  {#if invalid}
    <div id="{errorId}" class:bx--form-requirement="{true}">{invalidText}</div>
  {/if}
</div>

<style>
  .person-select {
    align-self: center;
    max-width: 100%;
    min-width: 0;
    padding: 0.5rem;
    margin: 0.25rem;
    border: 1px solid var(--pax-black-inactive);
    border-radius: 0.25rem;
    font: inherit;
    color: inherit;
    background: var(--pax-white, white);
  }

  .color-picker {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    border: 0;
    opacity: 0;
    pointer-events: none;
  }

  .toolbar :global(button[aria-pressed="true"]) {
    background-color: var(--pax-black-inactive);
    color: var(--pax-black);
    border-radius: 0;
  }

  .toolbar {
    align-items: center;
  }

  .content {
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    resize: none;
    min-width: 0;
    max-width: 100%;
    min-height: 2.6rem;
    line-height: 1.5;
    border-radius: 0 0 0.5rem 0.5rem;
    overflow: auto;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
    height: auto;
  }

  .content:empty::before {
    content: attr(data-placeholder);
    color: var(--pax-black-inactive);
    pointer-events: none;
  }

  .content :global(b),
  .content :global(strong) {
    font-weight: bold;
  }
  .content :global(i),
  .content :global(em) {
    font-style: italic;
  }
  .content :global(u) {
    text-decoration: underline;
  }
  .content :global(h1) {
    font-size: 2em;
    /* Heading controls size; bold remains an independent inline format. */
    font-weight: inherit;
  }
  textarea::placeholder {
    color: var(--pax-black-inactive);
  }

  textarea {
    box-sizing: border-box;
    flex: 1 1 auto;
    height: auto;
    max-width: 100%;
    min-width: 0;
    min-height: 2.6rem;
    resize: none;
    overflow: auto;
  }

  .bx--text-area__input-wrapper {
    box-sizing: border-box;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    border-radius: 0.5rem;
    border: 1px solid var(--pax-black-inactive);
    width: auto;
    max-width: 100%;
    min-width: min(10rem, 100%);
    min-height: 2.6rem;
  }

  .bx--text-area__wrapper {
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
  }

  .rich-text-frame {
    box-sizing: border-box;
    overflow: hidden;
    min-width: 10rem;
    min-height: 6rem;
    flex: 1 1 auto;
  }

  .rich-text-body {
    flex: 1 1 auto;
    min-height: 2.6rem;
    font-size: var(--cds-body-long-01-font-size, 0.875rem);
  }

  .bx--text-area__input-wrapper:focus-within {
    border-color: var(--pax-black);
  }

  .bx--text-area--richText {
    display: flex;
    flex-wrap: wrap;
    flex-shrink: 0;
    border-radius: 0.5rem 0.5rem 0rem 0rem;
    border-bottom: 1px solid var(--pax-black-inactive);
    background-color: #f7f7f7;
    /* gap: 0.5rem; */
  }

  .toolbar:not(.small-toolbar) :global(.bx--btn__icon) {
    width: 20px;
    height: 20px;
  }

  .bx--text-area__label-wrapper :global(.bx--select--inline) {
    justify-content: flex-end;
  }

  .bx--text-area__label-wrapper .bx--label {
    align-self: flex-end !important;
  }
  .select-wrapper {
    padding-bottom: 0.5rem;
  }
</style>
